import React, { useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import { Box, type BoxT } from '../box/tw';
import { Text } from '../text/tw';
import {
  TooltipContainer,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '../tooltip';

export interface CopyT extends Omit<BoxT, 'children'> {
  src: string;
  showSuccessTooltip?: boolean;
  tooltip?: string;
  children: (arg0: { copy: () => void }) => ReactNode;
}

export const Copy = ({
  src,
  showSuccessTooltip = true,
  tooltip,
  children,
  className,
  ...rest
}: CopyT) => {
  const t = useTranslations('ragnar.copy');
  const [show, setShow] = useState(false);

  const copyToClipboard = async () => {
    let failedToCopy = false;
    try {
      await navigator.clipboard.writeText(src);
    } catch (error) {
      failedToCopy = true;
    }

    if (showSuccessTooltip && !failedToCopy) {
      setShow(true);
      setTimeout(() => {
        setShow(false);
      }, 2000);
    }
  };

  return (
    <TooltipProvider>
      <TooltipRoot open={show}>
        <TooltipTrigger asChild>
          <Box className={className} {...rest}>
            {children({ copy: copyToClipboard })}
          </Box>
        </TooltipTrigger>
        <TooltipContainer side="top" withPortal={true}>
          <Text className="text-87-5 text-on-high-contrast">
            {tooltip ?? t('copy_success_tooltip')}
          </Text>
        </TooltipContainer>
      </TooltipRoot>
    </TooltipProvider>
  );
};
